import { cn } from '@/lib/utils';

/**
 * The rectangle every placeholder in the app is built from (spec 079 FR-001).
 *
 * **It sets no size of its own.** The caller gives it one, because the whole point of a
 * skeleton is to be the shape of a *particular* thing — a table row, a card, a chart
 * area — and a primitive that guessed would be wrong in every one of those places.
 *
 * **No `width`/`height` props**, deliberately. Sizes here are Tailwind utilities, the
 * same vocabulary every other component in this tree already speaks; a parallel numeric
 * API would be a second way to say one thing, and the two would drift.
 *
 * **`aria-hidden`.** This is decoration. A dozen announced grey boxes is noise, and the
 * announcement belongs once, to the region that is waiting — which is `PendingRegion`'s
 * job (FR-023). A skeleton that announces itself is how a visual upgrade turns into an
 * accessibility regression.
 *
 * The pulse stops for a reader who has asked for reduced motion, and not by the
 * mechanism you would expect: the global rule's `animation-duration: 0.01ms` alone would
 * make an infinite animation flicker *faster*. What stops it is the companion
 * `animation-iteration-count: 1`, which `globals.css` documents against this exact case
 * and `tests/shared/reducedMotionRule.test.ts` pins so it is not deleted as redundant.
 */
export function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      aria-hidden="true"
      data-slot="skeleton"
      className={cn('bg-muted animate-pulse rounded-md', className)}
      {...props}
    />
  );
}
